import React, { useState, useEffect } from 'react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  // Enhanced smooth scroll with offset for fixed header
  const smoothScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80; // Approximate header height
      const elementPosition = element.offsetTop - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { name: 'Speisekarte', id: 'menu' },
    { name: 'Galerie', id: 'gallery' },
    { name: 'Über Uns', id: 'about' },
    { name: 'Standort', id: 'location' },
    { name: 'Kontakt', id: 'contact' }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/90 backdrop-blur-md shadow-xl border-b border-white/20' 
          : 'bg-white/85 backdrop-blur-sm'
      }`}>
        <div className="container-custom">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src="/img/logo.png" 
                alt="Olive Fulda Logo" 
                className="h-12 w-auto cursor-pointer transition-all duration-300 hover:scale-110 hover:drop-shadow-lg"
                onClick={() => {
                  window.location.hash = '';
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => smoothScrollToSection(item.id)}
                className="relative text-gray-700 hover:text-primary font-semibold tracking-wide transition-all duration-300 capitalize group py-2 focus:outline-none focus:text-primary"
              >
                <span className="relative z-10">
                  {item.name}
                </span>
                {/* Animated underline */}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                {/* Hover background */}
                <span className="absolute inset-0 bg-primary/5 rounded-lg scale-0 transition-transform duration-300 group-hover:scale-100"></span>
              </button>
            ))}
          </nav>

          {/* Call Button & Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Enhanced Call Button */}
            <a
              href="tel:4966195258092"
              className="bg-primary text-white px-6 py-3 rounded-full font-semibold tracking-wide hover:bg-primary-light transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform active:scale-95 hidden md:flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span>Anrufen</span>
            </a>

            {/* Enhanced Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-3 text-gray-700 hover:text-primary transition-all duration-300 hover:bg-primary/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              aria-label="Toggle menu"
            >
              <div className="w-6 h-6 relative">
                <span className={`absolute block w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? 'rotate-45 top-3' : 'top-1'
                }`}></span>
                <span className={`absolute block w-6 h-0.5 bg-current transition-all duration-300 top-3 ${
                  isMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}></span>
                <span className={`absolute block w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? '-rotate-45 top-3' : 'top-5'
                }`}></span>
              </div>
            </button>
          </div>
        </div>

        </div>
      </header>

      {/* Enhanced Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
        isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        ></div>
        
        {/* Menu Content */}
        <div className={`absolute top-20 left-0 right-0 bg-white/95 backdrop-blur-md shadow-2xl border-b border-white/20 transition-all duration-300 ${
          isMenuOpen ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <nav className="container-custom py-8">
            <div className="flex flex-col space-y-6">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => smoothScrollToSection(item.id)}
                  className={`text-left text-gray-700 hover:text-primary font-semibold text-lg tracking-wide transition-all duration-300 capitalize py-3 px-4 rounded-lg hover:bg-primary/5 transform hover:translate-x-2 focus:outline-none focus:text-primary focus:bg-primary/5 ${
                    isMenuOpen ? 'animate-fade-in-up' : ''
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {item.name}
                </button>
              ))}
              
            </div>
          </nav>
        </div>
      </div>

      {/* Floating Action Button for Mobile */}
      <a
        href="tel:4966195258092"
        className={`md:hidden fixed bottom-6 right-6 bg-primary text-white p-4 rounded-full shadow-2xl z-50 transform transition-all duration-300 hover:scale-110 hover:bg-primary-light active:scale-95 ${isMenuOpen ? 'scale-0' : 'scale-100'}`}
        aria-label="Jetzt anrufen"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
        </svg>
      </a>
    </>
  );
};

export default Header;